/**
 * Search Result Container.
 * It helps to display the Search Result using Data List Component.
 * The `DataList` Component internally read the data and pass with pagination as per Config Props.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from 'kepler/lib/components/Button';
import _isEmpty from 'lodash/isEmpty';

// Components and Shapes.
import Notifier from '../../contactUs/containers/Notifier';
import { DataList } from '../../contactUs/components/common/index';
import MobileFilterModal from './MobileFilterModal';

// Actions.
import { mobileMenuOpen } from '../../contactUs/actions/action-mobile-filter';

// Helpers.
import Page from '../../lib/page';
import i18n from '../../lib/reacti18n';
import l10n from '../../lib/reactPPl10nHelper';
import { trackLink } from '../../lib/analytics-helper';
import contentHelper from '../../lib/contentHelper';
import { isC2MerchantMigrated } from '../../lib/globalHelper';

// Locales.
const searchResult = {
  title: i18n.message('smarthelp/searchResult.searchResult.title'),
  empty: i18n.message('smarthelp/searchResult.searchResult.empty'),
  subTitle: i18n.message('smarthelp/searchResult.searchResult.subTitle'),
  errorOccured: i18n.message('smarthelp/searchResult.searchResult.errorOccured'),
  tryAgain: i18n.message('smarthelp/searchResult.searchResult.tryAgain'),
  helpCenterLink: i18n.link('smarthelp/searchResult.searchResult.link.helpCenter'),
  noResults: i18n.message('smarthelp/searchResult.searchResult.noResults'),
  communityForumLink: i18n.link('smarthelp/searchResult.searchResult.link.communityForum'),
  altSearch: i18n.message('smarthelp/searchResult.searchResult.altSearch'),
  orSearch: i18n.message('smarthelp/searchResult.searchResult.orSearch'),
  sws: {
    title: l10n.getContent('smarthelp/searchResult.searchResult.sws.title'),
    head: l10n.getContent('smarthelp/searchResult.searchResult.sws.head'),
    body: l10n.getContent('smarthelp/searchResult.searchResult.sws.body'),
  },
};

const mobileFilter = {
  update: i18n.message('smarthelp/searchResult.searchResult.mobile.filter.update'),
};

const prev = i18n.message('smarthelp/pagination.prev');
const next = i18n.message('smarthelp/pagination.next');

const country = Page.getCountry();
const isMobileLayout = Page.isMobileLayout();
const helpCenterUri = `/${country}/smarthelp/home`;
const BRC_CHANNEL = 'brc';

class SearchResult extends Component {
  // This Method focus to the first search result item, if its exist.
  static focusOnFirstItem() {
    const firstItem = document.querySelector('div.search-result-list.data-list ul.list-items li:first-child a');
    if (firstItem) {
      firstItem.focus();
    }
  }

  // Setting focus on the search box
  static focusOnSearchBox() {
    const searchBox = document.querySelector('div.react-autosuggest__container input#help-input-text.search-input');
    if (searchBox) {
      searchBox.focus();
    }
  }

  // This Static method return `Data List Component` Locales as one Props.
  static getLabels() {
    return {
      prev,
      next,
    };
  }

  // List of Pagination Click Event Methods.
  static getClickMethods() {
    return {
      previous(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-previous-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
      pageNumber(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
      next(page) {
        trackLink({ id: 'searchPage', link: `smarthelp-search-result-next-page|${page}` });
        SearchResult.focusOnFirstItem();
      },
    };
  }

  static getPanelClass() {
    let classPanel = 'search-result-panel';
    if (isMobileLayout) {
      classPanel += ' mobile-panel';
    }
    return classPanel;
  }

  // Render Search Error or SWS page title and content
  static renderSearchInfo(options) {
    return (
      <span className={`result-stats ${options.type}`}>
        <span className="title">{options.title}</span>
        {options.body}
      </span>
    );
  }

  constructor(props) {
    super(props);

    this.state = {
      q: props.q,
    };
    this.clickHandlerFilterBtn = this.clickHandlerFilterBtn.bind(this);
  }

  // This Method return Search Term and truncate, if its lengthier than 20 characters.
  getSearchTerm() {
    const searchTerm = this.props.q;
    let strippedTerm = searchTerm.replace(/<[^>]+>/gi, '').trim();

    if (strippedTerm && strippedTerm.length > 20) {
      strippedTerm = `${strippedTerm.substr(0, 20)}...`;
    }
    return strippedTerm;
  }

  getCommunityLinkText() {
    // if the user is c2merchant migrated do not display community link in search results page
    if (Page.getCommunityLink() && !isC2MerchantMigrated()) {
      return (
        <span>
          {' '}
          {searchResult.orSearch}
          <a
            href={Page.getCommunityLink()}
            className="error-redirect"
            onClick={(e) => this.goToCommunityPage(e)}
            onContextMenu={() => this.trackFPTICommunity()}
            aria-label={searchResult.communityForumLink}
          >
            {searchResult.communityForumLink}
          </a>
        </span>
      );
    }
    return null;
  }

  /**
   * getResultNotes return the Result Notes as per Record Count.
   * Shows Record Count with Search Term, if there is records.
   * Shows No Record, if there is no records.
   * Show SWS message for search without query.
   */
  getResultNotes() {
    const searchTerm = this.getSearchTerm();

    let notes = <span className="result-stats empty" />;
    if (Array.isArray(this.props.items) && this.props.items.length > 0) {
      notes = (
        // <span
        //   className="result-stats"
        //   dangerouslySetInnerHTML={setInnerContent(
        //     l10n.getContent('smarthelp/searchResult.searchResult.subTitle', {
        //       resultCount: this.props.items.length,
        //       searchTerm,
        //     }),
        //   )}
        // />
        <div />
      );
    } else if (this.props.error && searchTerm) {
      const body = (
        <div className="error-body">
          {searchResult.altSearch}
          <a
            href={helpCenterUri}
            className="error-redirect"
            onClick={(e) => this.goToHelpCenter(e)}
            onContextMenu={() => this.trackFPTIHelp()}
            aria-label={searchResult.helpCenterLink}
          >
            {searchResult.helpCenterLink}
          </a>
          {this.getCommunityLinkText()}
        </div>
      );

      notes = SearchResult.renderSearchInfo({
        type: 'error',
        title: searchResult.noResults,
        body,
      });
    } else {
      // SWS research result / search without query
      const body = <div className="error-body">{searchResult.sws.body}</div>;

      notes = SearchResult.renderSearchInfo({
        type: 'info',
        title: searchResult.sws.head,
        body,
      });
      setTimeout(() => SearchResult.focusOnSearchBox(), 400);
    }

    return notes;
  }

  // This Method helps to read the `Search Result Response` and pass it as Data List Format.
  getListItems() {
    return this.props.items.map((item) => {
      let title = item.articleTitle;
      /**
       * TODO: Updated article URL with rank info which is from search result
       * let link = item.url;
       * link = `/${country}/smarthelp/article/${articleTitle}-${articleId.toLowerCase()}/${rank}`;
       */

      const articleTitle = Page.getFaqUrlFromTitle(title);
      const articleId = item.articleId;
      let link = item.url || item.link;
      // eslint-disable-next-line camelcase
      const a_track = item.a_track;
      const intents = item.intents || '';
      const customerSaysIntent = item.customerSaysIntent || '';
      if (articleId && typeof articleId === 'string' && articleId.includes('BRC') && link) {
        const slugId = link.split('/').pop();
        link = `/${country}/brc/article/${slugId}`;
      }
      if (Page.isCommunityChannel(item.channel) && item.views) {
        const communitySearchTitle = i18n.message('smarthelp/searchResult.searchResult.link.communityTitle', {
          title: item.articleTitle,
          views: item.views,
        });
        title = contentHelper(communitySearchTitle, { title: item.articleTitle, views: item.views });
      }

      // Check if `item.url` is undefined and it will be added only for `BRC`, `DEVELOPER`, `PRODUCT`.
      if (!link && !_isEmpty(articleId)) {
        link = `/${country}/smarthelp/article/${articleTitle}-${articleId.toLowerCase()}`;
      }
      const channel = item.channel;
      return {
        title,
        link,
        description: item.excerpt,
        articleId,
        rank: item.rank,
        intents,
        customerSaysIntent,
        // eslint-disable-next-line camelcase
        a_track,
        channel,
      };
    });
  }

  trackFPTIHelp() {
    const link = `smarthelp-search-result-error-redirect-smarthelp|${this.props.q}`;
    trackLink({ id: 'searchPage', link });
  }

  // This Method track the Help Center Link Click and redirect to Help Center Home.
  goToHelpCenter(e) {
    e.preventDefault();
    const link = `smarthelp-search-result-error-redirect-smarthelp|${this.props.q}`;
    trackLink({ id: 'searchPage', link });
    const cobrand = Page.getCountry();
    const redirectLink = `/${cobrand}/smarthelp/home`;
    Page.goTo(redirectLink);
  }

  trackFPTICommunity() {
    const link = `smarthelp-search-result-error-redirect-community|${this.props.q}`;
    trackLink({ id: 'searchPage', link });
  }

  goToCommunityPage(e) {
    e.preventDefault();
    const link = `smarthelp-search-result-error-redirect-community|${this.props.q}`;
    trackLink({ id: 'searchPage', link });
    Page.goTo(Page.getCommunityLink());
  }

  // Check the current channel filter which whether is BRC or not
  isBRCChannel() {
    return this.props.channel === BRC_CHANNEL;
  }

  clickHandlerFilterBtn() {
    this.props.mobileMenuOpen();
  }

  renderFilterButton() {
    return (
      <Button className="filter-btn" onClick={this.clickHandlerFilterBtn}>
        {mobileFilter.update}
      </Button>
    );
  }

  /**
   * renderSearchTitle render search title
   * 'Search Results' for search page with query
   * 'Search PayPal' for search page without query
   */
  renderSearchTitle() {
    const searchTerm = this.getSearchTerm();
    return <h1 className="search-result-title">{searchTerm ? searchResult.title : searchResult.sws.title}</h1>;
  }

  renderSearchPanel() {
    let rowClass = SearchResult.getPanelClass();
    // The Error page without filter button
    if (this.props.error) {
      rowClass = `${SearchResult.getPanelClass()} error`;
    }

    // The Panel Grid System which only be used at xs and sm
    return (
      <div className={rowClass}>
        <div className="result-notes-container">{this.getResultNotes()}</div>
        <div className="filter-btn-container">{this.renderFilterButton()}</div>
        <MobileFilterModal />
      </div>
    );
  }

  // Render the `SearchResult` Container.
  render() {
    return (
      <div id="search-result">
        <Notifier />
        {this.renderSearchTitle()}
        {this.renderSearchPanel()}
        <DataList
          parentClass="search-result-list"
          items={this.getListItems()}
          pageSize={10}
          labels={SearchResult.getLabels()}
          onClick={SearchResult.getClickMethods()}
          paginate
          showGoToPage={!isMobileLayout}
          searchQuery={this.props.q}
          HTMLTitle={this.isBRCChannel()}
        />
      </div>
    );
  }
}

// SearchResult Props Types.
SearchResult.propTypes = {
  // Method Prop Types.
  q: PropTypes.string.isRequired,
  error: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      description: PropTypes.string,
    }),
  ),
  mobileMenuOpen: PropTypes.func.isRequired,
  channel: PropTypes.string,
};

// SearchResult Props default values.
SearchResult.defaultProps = {
  q: '',
  error: false,
  items: [],
  channel: '',
};

// Dispatch the Action as Props.
function mapDispatchToProps(dispatch) {
  return bindActionCreators({ mobileMenuOpen }, dispatch);
}

// Get the Store data and Pass it as Props.
function mapStateToProps(state) {
  return {
    q: state.searchResult.q,
    error: state.searchResult.error,
    items: state.searchResult.items,
    channel: state.searchResult.channel,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SearchResult);
